import React, {useEffect} from 'react';
import moment from 'moment';
import {useHistory} from 'react-router-dom';
import styles from './AggregateDispatch.module.css';
import {
  Table,
  BackButton,
  DropToolBar,
} from '../../components';
import {
  AGGREGATES,
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  RESOURCE_TYPE,
  USER_OPERATIONS,
} from '../../constants';
import {statusStringMap} from '../../staticData';
import {withAllowedOperationsProvider} from '../../hocs';
import {API} from '../../apis/api';
import {usePlantAndLinesStore} from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import {usePopupManager} from '../../providers/PopupManager/PopupManager';
import {buildErrorMessage} from '../../apis/calls';
import PropTypes from 'prop-types';
function AggregateDispatch({
  aggregate,
  setAgsnCount,
  hasActiveEdits,
  setHasActiveEdits,
  rowsData,
  lastRefreshedDate,
  selectedDate,
  setSelectedDate,
  loadAggregateAgsnForDate,
  setHandleDispatchEdit,
  agsnDates
}) {
  const history = useHistory();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().aggregateLineState;
  const ROW_HEIGHT = 40;
  const {showPopup} = usePopupManager();

  const baseDefaultColumns = [
    {
      width: 120,
      title: 'AgSN',
      field: 'agsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'ASN',
      field: 'parentAsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: 'Part No.',
      field: 'materialNumber',
      enableSearch: true,
      enableFilter: true,
      render: row => (
        <>
          {aggregate !== AGGREGATES.MATERIAL && (
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                minWidth: '97px',
              }}
              status={row.materialStatus}
            >
              {row.materialNumber}
            </div>
          )}
        </>
      ),
    },
    {
      width: 210,
      title: 'Part Description',
      field: 'materialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Aggregate Status',
      field: 'aggregateStatus',
      render: row => {
        if (row.status === AGGREGATE_STATUS.ASN_GENERATED)
          return <span style={{opacity: 0.5}}>{statusStringMap[row.status]}</span>;
        return statusStringMap[row.status];
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: 'Vc No.',
      field: 'parentMaterialNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 210,
      title: 'Vc Description',
      field: 'parentMaterialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 130,
      title: 'Vc Status',
      field: 'vcStatus',
      render: row => {
        if (row.parentStatus === AGGREGATE_STATUS.ASN_GENERATED)
          return <span style={{opacity: 0.5}}>{statusStringMap[row.parentStatus]}</span>;
        return statusStringMap[row.parentStatus];
      },
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 100,
      title: 'Updated At',
      field: 'updatedAt',
      cellStyles: { padding: '2px 12px' },
      render: ({ updatedAt }) => (
        <div style={{ textAlign: 'center' }}>
          <div className={styles.time}>
            {updatedAt && moment(updatedAt).format('hh:mm A')}
          </div>
          <div className={styles.date}>
            {updatedAt &&
              moment(updatedAt).format('DD-MM-YYYY')}
          </div>
        </div>
      ),
    },
    {
      width: 150,
      title: 'Updated By',
      field: 'updatedBy',
      enableSearch: true,
      enableFilter: true,
    },
  ].filter(c => c);

  const redirectToApplicationLandingPage = () => {
    history.push('/');
  };

  const setCount = count => {
    setAgsnCount(count);
  };

  const dispatchBookedRows = async selectedRows => {
    try {
      const idsOfSelectedRows = selectedRows.map(res => {
        return res.id;
      });
      const payload = {
        ids: idsOfSelectedRows,
      };
      await API.patch(
        API_RESOURCE_URLS.aggregateDispatch(plant.value, line.value),
        payload,
      );
      loadAggregateAgsnForDate(plant.value, line.value, selectedDate);
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: DISPLAY_MESSAGES.AGGREGATE_DISPATCH_UPDATED,
        info: '',
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        error: err,
        contextText: DISPLAY_MESSAGES.AGGREGATE_DISPATCH_FAILED,
      });
    }
  };

  useEffect(() => {
    setHandleDispatchEdit(hasActiveEdits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveEdits]);
  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath={aggregate}
          action="Dispatch"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <DropToolBar
        activeDate={selectedDate}
        dates={agsnDates}
        handleDateSelect={date => setSelectedDate(date)}
        lastRefreshedDate={lastRefreshedDate}
        contextualButtonProps={{
          text: 'PREVIOUS',
          label: 'Prev',
          handleClick: () => setSelectedDate(moment().subtract(1, 'days')),
        }}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
        showAggregateLines
      />
      <div className={styles.container}>
        <div className={styles.tableView} style={{height: '100%'}}>
          <Table
            columns={[...baseDefaultColumns]}
            defaultColumns={[...baseDefaultColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.data}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            displayHash
            primaryAction={{
              name: 'Dispatch',
              actionFn: selectedRows => dispatchBookedRows(selectedRows),
              authOperation: USER_OPERATIONS.AGGREGATE_DISPATCH,
              shouldEnable: selectedRows =>
                selectedRows.length &&
                selectedRows.every(
                  row => row.status === AGGREGATE_STATUS.BOOKED && row.destination !== null && row.destination !== undefined,
                ),
            }}
          />
        </div>
      </div>
    </>
  );
}
AggregateDispatch.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  aggregate: PropTypes.string.isRequired,
  lastRefreshedDate: PropTypes.objectOf(moment),
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      filteredData: PropTypes.array.isRequired,
    }),
  ),
  setSelectedDate: PropTypes.func.isRequired,
  selectedDate: PropTypes.objectOf(moment),
  loadAggregateAgsnForDate: PropTypes.func.isRequired,
  setHandleDispatchEdit: PropTypes.func.isRequired,
  setAgsnCount: PropTypes.func.isRequired,
  agsnDates: PropTypes.array.isRequired
};
export default withAllowedOperationsProvider(
  AggregateDispatch,
  true,
  RESOURCE_TYPE.MATERIAL,
);
